import { TrashIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';

import TableLayout, { TableNullState } from 'dashboard/layouts/TableLayout';
import {
  ExternalIntegration,
  ExternalIntegrationStatus,
} from 'dashboard/models/external-integration';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Sticker, { StickerProps } from 'design-system/components/Sticker';
import Table, {
  TableBody,
  TableCell,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';

const INTEGRATION_STICKER_MAP: Record<
  ExternalIntegrationStatus,
  { stickerVariant: StickerProps['variant']; message: string }
> = {
  [ExternalIntegrationStatus.Initialized]: { stickerVariant: 'caution', message: 'Initialized' },
  [ExternalIntegrationStatus.Active]: { stickerVariant: 'success', message: 'Active' },
  [ExternalIntegrationStatus.Inactive]: { stickerVariant: 'caution', message: 'Inactive' },
  [ExternalIntegrationStatus.Unhealthy]: { stickerVariant: 'caution', message: 'Unhealthy' },
  [ExternalIntegrationStatus.Archived]: { stickerVariant: 'danger', message: 'Archived' },
};

interface ExternalIntegrationOAuthDetailsTableProps {
  isLoading: boolean;
  editable: boolean;
  displayName: string;
  connectionName: string;
  externalIntegrations: ExternalIntegration[];
  setSelectedIntegration: (integration: ExternalIntegration) => void;
}

const ExternalIntegrationOAuthDetailsTable = ({
  isLoading,
  editable,
  displayName,
  connectionName,
  externalIntegrations,
  setSelectedIntegration,
}: ExternalIntegrationOAuthDetailsTableProps) => {
  return (
    <>
      <Card>
        <Card.Body>
          <Table>
            <TableLayout
              isLoading={isLoading}
              isEmpty={externalIntegrations.length === 0}
              showHeader={true}
              nullState={
                <TableNullState>
                  Looks like there aren&apos;t any {displayName} connections setup yet
                </TableNullState>
              }
              headerTR={<Card.Title>Connections</Card.Title>}
            >
              <TableRow>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>{connectionName}</TableHeaderCell>
                <TableHeaderCell>Created by</TableHeaderCell>
                <TableHeaderCell>{`Created at (${DateTime.local().toFormat(
                  TIMEZONE_NAME_ABBREVIATED,
                )})`}</TableHeaderCell>
                <TableHeaderCell>{/* Archive item column */}</TableHeaderCell>
              </TableRow>
              <TableBody>
                {externalIntegrations.map((integration) => (
                  <TableRow key={`external-integration-${integration.id}`}>
                    <TableCell>
                      <Sticker
                        variant={
                          INTEGRATION_STICKER_MAP[integration.attributes.status].stickerVariant
                        }
                      >
                        {INTEGRATION_STICKER_MAP[integration.attributes.status].message}
                      </Sticker>
                    </TableCell>
                    <TableCell>{integration.attributes.name}</TableCell>
                    <TableCell>{integration.attributes['creator-name']}</TableCell>
                    <TableCell>
                      {DateTime.fromISO(integration.attributes['created-at']).toFormat(
                        DATE_AND_TIME_FORMAT,
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        width='icon'
                        variant='basic'
                        type='button'
                        onClick={() => setSelectedIntegration(integration)}
                        disabled={!editable}
                        id={`delete-button-${integration.id}`}
                        aria-label='delete button'
                      >
                        <TrashIcon />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TableLayout>
          </Table>
        </Card.Body>
      </Card>
    </>
  );
};

export default ExternalIntegrationOAuthDetailsTable;
